import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { SelectLocationFull, SelectReservation } from '@workspace/db/extra'
import { z } from 'zod'
import { ApiError } from '@/lib/api-client'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'
import { locationQueryKey, useLocationSelect } from './use-location'

const okEnvelopeSchema = z.object({ success: z.boolean() }).passthrough()

/**
 * DHCP reservations for the store. Table data is the location's nested reservations (manifest: v1 reads them from the
 * layout location query, `withReservations`), so this shares the `useLocation` cache entry.
 */
export function useReservations(idOrCode: string) {
  return useLocationSelect(idOrCode, locationReservations)
}

/** Pure: reservations live at `networkProfiles[].vendorSites[].vendorSiteNetworks[].reservations`; flatten them. */
export function locationReservations(loc: SelectLocationFull | null): SelectReservation[] {
  return (loc?.networkProfiles ?? []).flatMap((np) =>
    (np.vendorSites ?? []).flatMap((vs) => (vs.vendorSiteNetworks ?? []).flatMap((n) => n.reservations ?? [])),
  )
}

export interface ReservationInput {
  /** Aptos store code (the API's `Code` query param). */
  code: string
  mac: string
  type: string
  vlan: string | number
  /** Override a conflict (API `force=true`). */
  force?: boolean
  /**
   * Cross-store move confirmation (API `confirmation=yes`). A `force` add onto a MAC reserved at another store
   * answers HTTP 428 until this is `'yes'` (see `isReservationConfirmationRequired`).
   */
  confirmation?: 'yes' | 'no'
}

/** Pure: path + query for POST /v1/reservations (`Code`, `mac`, `type`, `vlan`[, `force`, `confirmation`]). */
export function reservationCreatePath(r: ReservationInput): string {
  const q = new URLSearchParams({ Code: r.code, mac: r.mac, type: r.type, vlan: String(r.vlan) })
  if (r.force) q.set('force', 'true')
  if (r.confirmation) q.set('confirmation', r.confirmation)
  return `/v1/reservations?${q.toString()}`
}

/** Pure: path + query for DELETE /v1/reservations (`mac`). */
export const reservationDeletePath = (mac: string): string =>
  `/v1/reservations?${new URLSearchParams({ mac }).toString()}`

/** Pure: v1 edit semantics: new vlan hard-coded `100`, type lowercased. */
export function editedReservationInput(code: string, mac: string, type: string, force?: boolean): ReservationInput {
  return { code, mac, type: type.toLowerCase(), vlan: 100, ...(force ? { force } : {}) }
}

/** Pure: a 409 is a reservation conflict the design resolves with the force dialog. */
export const isReservationConflict = (err: unknown): boolean => err instanceof ApiError && err.status === 409

/** Pure: a 428 is the cross-store move prompt; re-submit the same item with `force: true, confirmation: 'yes'`. */
export const isReservationConfirmationRequired = (err: unknown): boolean =>
  err instanceof ApiError && err.status === 428

export interface BatchResult {
  processedCount: number
  /** Set when the batch stopped on a conflict (resolve, then call again with the remaining items). */
  conflict?: { index: number; error: ApiError }
}

function useInvalidateReservations() {
  const qc = useQueryClient()
  return (idOrCode: string) => qc.invalidateQueries({ queryKey: locationQueryKey(idOrCode) })
}

/** Add one reservation (POST /v1/reservations). `mutate({ idOrCode, reservation })`. 409 -> `isReservationConflict`; retry with `force: true`. */
export function useAddReservation() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidateReservations()
  return useMutation({
    mutationFn: async (v: { idOrCode: string; reservation: ReservationInput }) => {
      if (preview) return { success: true }
      return api.request(reservationCreatePath(v.reservation), { method: 'POST' }, okEnvelopeSchema)
    },
    onSuccess: (_r, v) => (preview ? undefined : invalidate(v.idOrCode)),
  })
}

/** Delete one reservation by MAC (DELETE /v1/reservations). `mutate({ idOrCode, mac })`. */
export function useDeleteReservation() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidateReservations()
  return useMutation({
    mutationFn: async (v: { idOrCode: string; mac: string }) => {
      if (preview) return { success: true }
      return api.request(reservationDeletePath(v.mac), { method: 'DELETE' }, okEnvelopeSchema)
    },
    onSuccess: (_r, v) => (preview ? undefined : invalidate(v.idOrCode)),
  })
}

/**
 * Batch add: N sequential POSTs, stopping on the first 409 conflict (returns `processedCount` + `conflict`).
 * Non-conflict errors throw. `mutate({ idOrCode, reservations })`.
 */
export function useAddReservationsBatch() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidateReservations()
  return useMutation({
    mutationFn: async (v: { idOrCode: string; reservations: ReservationInput[] }): Promise<BatchResult> => {
      if (preview) return { processedCount: v.reservations.length }
      let processedCount = 0
      for (const [index, r] of v.reservations.entries()) {
        try {
          await api.request(reservationCreatePath(r), { method: 'POST' }, okEnvelopeSchema)
          processedCount++
        } catch (error) {
          if (isReservationConflict(error)) return { processedCount, conflict: { index, error: error as ApiError } }
          throw error
        }
      }
      return { processedCount }
    },
    onSettled: (_r, _e, v) => (preview ? undefined : invalidate(v.idOrCode)),
  })
}

/**
 * Batch edit / move: per change DELETE the old MAC then POST the new one (v1 semantics: vlan 100, type lowercased).
 * Stops on a conflict like the batch add. `mutate({ idOrCode, changes: [{ oldMac, next: ReservationInput }] })`.
 */
export function useEditReservationsBatch() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidateReservations()
  return useMutation({
    mutationFn: async (v: {
      idOrCode: string
      changes: Array<{ oldMac: string; next: ReservationInput }>
    }): Promise<BatchResult> => {
      if (preview) return { processedCount: v.changes.length }
      let processedCount = 0
      for (const [index, c] of v.changes.entries()) {
        try {
          await api.request(reservationDeletePath(c.oldMac), { method: 'DELETE' }, okEnvelopeSchema)
          await api.request(reservationCreatePath(c.next), { method: 'POST' }, okEnvelopeSchema)
          processedCount++
        } catch (error) {
          if (isReservationConflict(error)) return { processedCount, conflict: { index, error: error as ApiError } }
          throw error
        }
      }
      return { processedCount }
    },
    onSettled: (_r, _e, v) => (preview ? undefined : invalidate(v.idOrCode)),
  })
}

/** "Refresh" button: GET /v1/reservations?locationCode=<aptos>, then refetch the location. `mutate({ idOrCode, aptosCode })`. */
export function useRefreshReservations() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidateReservations()
  return useMutation({
    mutationFn: async (v: { idOrCode: string; aptosCode: string }) => {
      if (preview) return { success: true }
      return api.get(
        `/v1/reservations?${new URLSearchParams({ locationCode: v.aptosCode }).toString()}`,
        okEnvelopeSchema,
      )
    },
    onSuccess: (_r, v) => (preview ? undefined : invalidate(v.idOrCode)),
  })
}
